import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Star, Trash2, Phone, CalendarPlus } from "lucide-react";
import { toast } from "sonner";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useAuth } from "@/context/AuthContext";
import { api, messageErreur } from "@/lib/api";
import { formatEuros } from "@/lib/format";
import { PastilleEtape } from "@/components/commun";
import { JournalEntree } from "@/pages/parametres/Journal";
import { EtapeMenu } from "@/components/fiche/EtapeMenu";
import { TacheDialog } from "@/components/fiche/TacheDialog";
import { ModifierDialog } from "@/components/fiche/ModifierDialog";
import { ContactCarte } from "@/components/fiche/ContactCarte";
import { BoutonScript } from "@/components/scripts/BoutonScript";
import { BoutonVisio } from "@/components/visio/BoutonVisio";
import { SouffleurOuverture } from "@/components/scripts/Souffleur";
import { ProchaineAction, FilActivite } from "@/components/fiche/Activite";
import { useMesVues } from "@/components/vues/commun";
import { FicheMouvement } from "@/components/vues/FicheMouvement";
import { DocumentsFiche } from "@/components/fiche/DocumentsFiche";
import { ConfirmationPanel } from "@/components/confirmation/ConfirmationPanel";
import { DevisFiche } from "@/components/partenaire/DevisFiche";
import { DiscussionFiche } from "@/components/partenaire/DiscussionFiche";
import { RetourPartenaire } from "@/components/tableaux/Retours";
import { OutilsEpingles } from "@/components/outils/HoteOutil";
import { EditeurDevis, CommissionFiche } from "@/components/devis/EditeurDevis";
import { initialesDe } from "@/components/messagerie/Wa";

export const Badges = ({ fiche }) => (
  <>
    {fiche.statut !== "en_cours" && <span className="px-2.5 py-1 at-pill text-xs font-bold" style={{ background: "var(--surface-2)" }} data-testid="fiche-statut">{fiche.statut_label}{fiche.motif_perte ? ` · ${fiche.motif_perte}` : ""}</span>}
    {fiche.sans_action && <span className="px-2.5 py-1 at-pill text-xs font-bold" style={{ background: "color-mix(in srgb, var(--danger) 14%, transparent)", color: "var(--danger)" }} data-testid="badge-sans-action">Sans action</span>}
    {fiche.fait_par_ia && <span className="px-2.5 py-1 at-pill text-xs font-bold" style={{ background: "color-mix(in srgb, var(--micro) 30%, transparent)" }} data-testid="badge-fait-par-ia">Fait par l'IA</span>}
    {fiche.en_retard && <span className="px-2.5 py-1 at-pill text-xs font-bold" style={{ background: "color-mix(in srgb, var(--temp-3) 16%, transparent)", color: "var(--temp-3)" }} data-testid="badge-en-retard">Délai dépassé</span>}
  </>
);

export default function FicheDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [fiche, setFiche] = useState(null);
  const [erreur, setErreur] = useState("");
  const [activite, setActivite] = useState([]);
  const [historique, setHistorique] = useState([]);
  const [champs, setChamps] = useState([]);
  const [dialogue, setDialogue] = useState("");
  const voitJournal = user.navigation.parametres.includes("journal");
  const vuesFiche = useMesVues("fiche");

  const charger = useCallback(() => {
    api.get(`/fiches/${id}`).then((r) => setFiche(r.data)).catch((e) => setErreur(messageErreur(e)));
    api.get(`/fiches/${id}/activite`).then((r) => setActivite(r.data)).catch(() => {});
    if (voitJournal) api.get(`/fiches/${id}/historique`).then((r) => setHistorique(r.data)).catch(() => {});
  }, [id, voitJournal]);
  useEffect(() => { charger(); api.get("/champs").then((r) => setChamps(r.data)); }, [charger]);
  useEffect(() => { window.addEventListener("assistant:maj", charger); return () => window.removeEventListener("assistant:maj", charger); }, [charger]);

  const changerEtape = async (etape, motif) => {
    try {
      const { data } = await api.post(`/fiches/${id}/etape`, { etape, motif });
      if (data.id) { toast.success(`Étape : ${data.etape_label}`); charger(); } else { toast.success("Dossier mis à jour."); navigate("/fiches"); }
    } catch (e) { toast.error(messageErreur(e)); }
  };
  const favori = async () => { const { data } = await api.post(`/fiches/${id}/favori`); setFiche((f) => ({ ...f, est_favori: data.est_favori })); };
  const supprimer = async () => {
    try { const { data } = await api.delete(`/fiches/${id}`); toast.success(data.message); navigate("/fiches"); } catch (e) { toast.error(messageErreur(e)); }
  };

  if (erreur) return <div className="at-card p-8" data-testid="fiche-error"><p className="font-semibold">{erreur}</p><Link to="/fiches" className="at-btn at-btn-ghost mt-4">Retour aux fiches</Link></div>;
  if (!fiche) return null;
  const tel = fiche.contact?.telephones?.[0];
  return (
    <div data-testid="page-fiche-detail" className="pb-24 md:pb-0">
      <Link to="/fiches" className="inline-flex items-center gap-2 min-h-[44px] font-semibold text-sm" data-testid="fiche-back-link"><ArrowLeft size={16} />Fiches</Link>
      <header className="at-card overflow-hidden mb-4">
        <div className="h-2" style={{ background: fiche.temperature || "var(--muted)" }} />
        <div className="p-5 md:p-7 flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div className="min-w-0">
            <div className="flex flex-wrap gap-2 items-center"><PastilleEtape fiche={fiche} testId="fiche-detail-etape" /><span className="text-xs at-muted font-semibold">{fiche.processus_nom}</span><Badges fiche={fiche} /></div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight mt-3 break-words" data-testid="fiche-detail-nom">{fiche.nom}</h1>
            <p className="at-muted mt-1">{[fiche.organisation && fiche.contact_nom, fiche.operation, fiche.commune, fiche.agent_nom, fiche.partenaire_nom].filter(Boolean).join(" · ")}</p>
          </div>
          <div className="flex items-center gap-2 md:flex-col md:items-end">
            <p className="text-2xl md:text-3xl font-bold" data-testid="fiche-detail-montant">{formatEuros(fiche.montant_estime)}</p>
            {fiche.commission !== undefined && <p className="text-sm at-muted" data-testid="fiche-detail-commission">Commission {formatEuros(fiche.commission)}</p>}
            <div className="flex gap-1 ml-auto md:ml-0">
              <button type="button" onClick={favori} className="at-icon-btn" aria-label="Favori" data-testid="fiche-favorite-button"><Star size={19} fill={fiche.est_favori ? "currentColor" : "none"} /></button>
              {fiche.peut_supprimer && <button type="button" onClick={() => setDialogue("supprimer")} className="at-icon-btn" aria-label="Corbeille" data-testid="fiche-delete-button"><Trash2 size={18} /></button>}
            </div>
          </div>
        </div>
      </header>
      <div className="wa-fiche-grille lg:grid lg:grid-cols-[380px_1fr] gap-4 space-y-4 lg:space-y-0 items-start">
        <div className="min-w-0">
          <div className="wa-seulement flex flex-col items-center text-center pt-6 pb-4" data-testid="fiche-wa-avatar">
            <span className="wa-rond" style={{ width: 120, height: 120, fontSize: 40, background: "var(--menu-fond, var(--accent))", color: "var(--sur-primaire, var(--accent-ink))" }}>{initialesDe(fiche.contact_nom || fiche.nom)}</span>
            <p className="text-xl font-semibold mt-3">{fiche.contact_nom || fiche.nom}</p>
            <p className="text-sm at-muted">{fiche.nom} · {fiche.commune}</p>
          </div>
          <ContactCarte fiche={fiche} champs={champs} onModifier={() => setDialogue("modifier")} onPlanifier={() => setDialogue("tache")} />
        </div>
        <div className="space-y-4 min-w-0">
          {fiche.statut === "en_cours" && (
            <div className="space-y-2">
              <div className={`grid gap-2 ${user.role !== "partenaire" ? "grid-cols-2" : "grid-cols-1"}`}>
                {user.role !== "partenaire" && <BoutonScript operation={fiche.operation} contact={fiche.contact_nom} testId="fiche-script-button" className="at-btn at-btn-ghost w-full" />}
                <BoutonVisio ficheId={fiche.id} testId="fiche-visio-button" className="at-btn at-btn-ghost w-full" />
              </div>
              {user.role !== "partenaire" && <SouffleurOuverture key={fiche.id} url={`/souffleur/fiche/${fiche.id}`} testId="fiche-prompter" />}
            </div>
          )}
          <ProchaineAction fiche={fiche} onPlanifier={() => setDialogue("tache")} onMaj={charger} />
          <ConfirmationPanel key={`${fiche.etape}-${fiche.statut}`} fiche={fiche} onMaj={charger} />
          <EditeurDevis fiche={fiche} onMaj={charger} />
          <DevisFiche fiche={fiche} onMaj={charger} />
          {user.role === "partenaire" && <RetourPartenaire fiche={fiche} onMaj={charger} />}
          <OutilsEpingles lieu="fiche" contexte={{ fiche_id: fiche.id }} titre="Outils" />
          <CommissionFiche key={`c-${fiche.commission}`} fiche={fiche} onMaj={charger} />
          <DocumentsFiche fiche={fiche} onMaj={charger} />
          <DiscussionFiche fiche={fiche} />
          {vuesFiche.vue === "fiche_mouvement" && <FicheMouvement fiche={fiche} onMaj={charger} onAvancer={(code) => changerEtape(code)} />}
          <div className="hidden md:block"><EtapeMenu etapes={fiche.etapes_possibles} actuelle={fiche.etape} onChoisir={changerEtape} /></div>
          <FilActivite ficheId={id} items={activite} onMaj={charger} />
          {voitJournal && (
            <section className="at-card p-5" data-testid="fiche-history">
              <p className="at-label mb-3">Historique des modifications</p>
              {historique.length === 0 ? <p className="at-muted text-sm">Aucune modification.</p> : historique.slice(0, 20).map((e) => <JournalEntree key={e.id} entree={e} />)}
            </section>
          )}
        </div>
      </div>
      <div className="md:hidden fixed bottom-[68px] inset-x-0 px-4 py-3 flex gap-2 z-30 backdrop-blur-xl" style={{ background: "color-mix(in srgb, var(--bg) 82%, transparent)" }}>
        <a href={tel ? `tel:${tel.replace(/\s/g, "")}` : undefined} className="at-btn at-btn-primary flex-1" data-testid="fiche-mobile-call"><Phone size={16} />Appeler</a>
        <EtapeMenu etapes={fiche.etapes_possibles} actuelle={fiche.etape} onChoisir={changerEtape} testId="mobile-stage-menu" label="Étape" className="flex-1 !px-3" />
        <button type="button" onClick={() => setDialogue("tache")} className="at-icon-btn at-btn-ghost shrink-0" aria-label="Planifier" data-testid="fiche-mobile-plan"><CalendarPlus size={18} /></button>
      </div>
      <TacheDialog ouvert={dialogue === "tache"} setOuvert={(o) => setDialogue(o ? "tache" : "")} ficheId={id} onCree={charger} />
      <ModifierDialog ouvert={dialogue === "modifier"} setOuvert={(o) => setDialogue(o ? "modifier" : "")} fiche={fiche} champs={champs} onEnregistre={charger} />
      <AlertDialog open={dialogue === "supprimer"} onOpenChange={(o) => setDialogue(o ? "supprimer" : "")}>
        <AlertDialogContent className="at-popover max-w-[calc(100vw-24px)] sm:max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>Mettre cette fiche à la corbeille ?</AlertDialogTitle>
            <AlertDialogDescription className="at-muted">Elle pourra être restaurée pendant 30 jours.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel className="at-btn at-btn-ghost border-0">Annuler</AlertDialogCancel>
            <AlertDialogAction className="at-btn" style={{ background: "var(--danger)", color: "var(--accent-ink)" }} onClick={supprimer} data-testid="fiche-delete-confirm">Mettre à la corbeille</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
